import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const STORAGE_KEY = "fixid_phase_timer_v1";
const MIN_DURATION_SECONDS = 3; // discard accidental <3s timings

export interface PhaseTimerState {
  phaseCategory: string;
  phaseLabel: string;
  applianceId?: string | null;
  applianceType?: string | null;
  brand?: string | null;
  startedAt: number; // epoch ms
  lastActiveAt: number; // epoch ms
  batchId?: string | null;
  extraApplianceIds: string[];
  pausedAt?: number | null; // epoch ms when paused, null if running
  pausedAccumMs: number; // total time previously spent paused
}

interface PhaseTimerContextValue {
  active: PhaseTimerState | null;
  elapsedSeconds: number;
  isPaused: boolean;
  startPhase: (params: {
    phaseCategory: string;
    phaseLabel: string;
    applianceId?: string | null;
    applianceType?: string | null;
    brand?: string | null;
  }) => void;
  stopPhase: () => Promise<void>;
  pausePhase: () => void;
  resumePhase: () => void;
  addApplianceToBatch: (applianceId: string) => Promise<void>;
}

const PhaseTimerContext = createContext<PhaseTimerContextValue | undefined>(undefined);

function loadPersisted(): PhaseTimerState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.phaseCategory || !parsed?.startedAt) return null;
    return parsed as PhaseTimerState;
  } catch {
    return null;
  }
}

function persist(state: PhaseTimerState | null) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

async function getCompanyId(userId: string | undefined): Promise<string | null> {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("user_companies")
      .select("company_id")
      .eq("user_id", userId)
      .maybeSingle();
    if (error || !data) return null;
    return data.company_id;
  } catch {
    return null;
  }
}

export const PhaseTimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [active, setActive] = useState<PhaseTimerState | null>(() => loadPersisted());
  const [now, setNow] = useState<number>(Date.now());
  const flushingRef = useRef(false);

  // Tick every second while active for elapsed display
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  // Track user activity (idle auto-pause)
  useEffect(() => {
    if (!active) return;
    const markActive = () => {
      setActive((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, lastActiveAt: Date.now() };
        persist(updated);
        return updated;
      });
    };
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, markActive));
  }, [active]);

  // Auto-stop if idle too long
  useEffect(() => {
    if (!active) return;
    const checkIdle = () => {
      if (!active) return;
      if (active.pausedAt) return; // don't auto-stop while explicitly paused
      if (Date.now() - active.lastActiveAt > IDLE_TIMEOUT_MS) {
        void stopInternal(active, "idle");
      }
    };
    const id = window.setInterval(checkIdle, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Flush on unload
  useEffect(() => {
    const handler = () => {
      if (active) persist(active);
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  const stopInternal = useCallback(async (state: PhaseTimerState, _reason: "manual" | "switch" | "idle") => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    try {
      // If currently paused, freeze "now" at pausedAt; otherwise cap to last activity + idle window
      const rawEndedAt = state.pausedAt ?? Math.min(Date.now(), state.lastActiveAt + IDLE_TIMEOUT_MS);
      const totalElapsedMs = rawEndedAt - state.startedAt - (state.pausedAccumMs ?? 0);
      const durationSeconds = Math.max(0, Math.round(totalElapsedMs / 1000));
      setActive(null);
      persist(null);

      if (durationSeconds < MIN_DURATION_SECONDS) return;

      const companyId = await getCompanyId(user?.id);
      if (!companyId) return;

      const appliances = [state.applianceId, ...state.extraApplianceIds].filter(Boolean) as string[];
      const uniqueAppliances = Array.from(new Set(appliances));

      const rows = (uniqueAppliances.length > 0 ? uniqueAppliances : [null]).map((aid) => ({
        phase_category: state.phaseCategory,
        appliance_type: state.applianceType ?? null,
        brand: state.brand ?? null,
        duration_seconds: durationSeconds,
        started_at: new Date(state.startedAt).toISOString(),
        ended_at: new Date(rawEndedAt).toISOString(),
        company_id: companyId,
        batch_id: state.batchId ?? null,
      }));

      await supabase.from("phase_time_logs" as any).insert(rows as any);

      const mins = Math.floor(durationSeconds / 60);
      const secs = durationSeconds % 60;
      toast(`Phase logged anonymously · ${mins > 0 ? `${mins}m ` : ""}${secs}s`, {
        description: "Times are stored anonymously and cannot be traced back to individuals.",
      });
    } catch {
      // silent — never block technician
    } finally {
      flushingRef.current = false;
    }
  }, [user?.id]);

  const startPhase = useCallback<PhaseTimerContextValue["startPhase"]>((params) => {
    setActive((prev) => {
      // If same phase + same primary appliance, no-op
      if (
        prev &&
        prev.phaseCategory === params.phaseCategory &&
        prev.applianceId === (params.applianceId ?? null)
      ) {
        return prev;
      }
      // Stop previous silently (fire-and-forget)
      if (prev) void stopInternal(prev, "switch");

      const next: PhaseTimerState = {
        phaseCategory: params.phaseCategory,
        phaseLabel: params.phaseLabel,
        applianceId: params.applianceId ?? null,
        applianceType: params.applianceType ?? null,
        brand: params.brand ?? null,
        startedAt: Date.now(),
        lastActiveAt: Date.now(),
        batchId: null,
        extraApplianceIds: [],
        pausedAt: null,
        pausedAccumMs: 0,
      };
      persist(next);
      return next;
    });
  }, [stopInternal]);

  const pausePhase = useCallback(() => {
    setActive((prev) => {
      if (!prev || prev.pausedAt) return prev;
      const updated: PhaseTimerState = { ...prev, pausedAt: Date.now() };
      persist(updated);
      return updated;
    });
  }, []);

  const resumePhase = useCallback(() => {
    setActive((prev) => {
      if (!prev || !prev.pausedAt) return prev;
      const pausedDuration = Date.now() - prev.pausedAt;
      const updated: PhaseTimerState = {
        ...prev,
        pausedAt: null,
        pausedAccumMs: (prev.pausedAccumMs ?? 0) + pausedDuration,
        lastActiveAt: Date.now(),
      };
      persist(updated);
      return updated;
    });
  }, []);

  const stopPhase = useCallback(async () => {
    if (!active) return;
    await stopInternal(active, "manual");
  }, [active, stopInternal]);

  const addApplianceToBatch = useCallback(async (applianceId: string) => {
    if (!active || !user?.id) return;
    if (active.applianceId === applianceId || active.extraApplianceIds.includes(applianceId)) return;

    try {
      let batchId = active.batchId;
      const companyId = await getCompanyId(user.id);
      if (!companyId) return;

      if (!batchId) {
        const { data, error } = await supabase
          .from("phase_time_batches" as any)
          .insert({ company_id: companyId } as any)
          .select("id")
          .single();
        if (error || !data) return;
        batchId = (data as any).id;

        // Link primary appliance too (if any)
        if (active.applianceId) {
          await supabase.from("phase_time_batch_appliances" as any).insert({
            batch_id: batchId,
            appliance_id: active.applianceId,
          } as any);
        }
      }

      await supabase.from("phase_time_batch_appliances" as any).insert({
        batch_id: batchId,
        appliance_id: applianceId,
      } as any);

      setActive((prev) => {
        if (!prev) return prev;
        const updated: PhaseTimerState = {
          ...prev,
          batchId,
          extraApplianceIds: [...prev.extraApplianceIds, applianceId],
        };
        persist(updated);
        return updated;
      });
    } catch {
      // silent
    }
  }, [active, user?.id]);

  const elapsedSeconds = useMemo(() => {
    if (!active) return 0;
    const endRef = active.pausedAt ?? now;
    const ms = endRef - active.startedAt - (active.pausedAccumMs ?? 0);
    return Math.max(0, Math.floor(ms / 1000));
  }, [active, now]);

  const isPaused = !!active?.pausedAt;

  const value = useMemo<PhaseTimerContextValue>(() => ({
    active,
    elapsedSeconds,
    isPaused,
    startPhase,
    stopPhase,
    pausePhase,
    resumePhase,
    addApplianceToBatch,
  }), [active, elapsedSeconds, isPaused, startPhase, stopPhase, pausePhase, resumePhase, addApplianceToBatch]);

  return <PhaseTimerContext.Provider value={value}>{children}</PhaseTimerContext.Provider>;
};

export const usePhaseTimerContext = () => {
  const ctx = useContext(PhaseTimerContext);
  if (!ctx) throw new Error("usePhaseTimerContext must be used within PhaseTimerProvider");
  return ctx;
};
